import React from "react";
import CodeBlock from "../components/CodeBlock";

const SseStreaming = () => (
  <section id="sse-streaming" className="developer-section">
    <h2>Working with /search and SSE Streaming</h2>
    <p>
      Plex uses Server-Sent Events (SSE) for real-time delivery of search
      results and LLM responses. This section covers the streaming protocol in
      detail.
    </p>

    <h3>Connection Lifecycle</h3>
    <ol className="dev-steps-list">
      <li>
        <strong>Initiate</strong> &mdash; Send a search request with{" "}
        <code>stream: true</code>. The server responds immediately with a{" "}
        <code>stream_id</code>.
      </li>
      <li>
        <strong>Connect</strong> &mdash; Open an SSE connection to{" "}
        <code>/api/stream/&#123;stream_id&#125;</code>. The server begins
        sending events.
      </li>
      <li>
        <strong>Receive</strong> &mdash; Process events as they arrive. Search
        events deliver progress and results; LLM events deliver streamed text.
      </li>
      <li>
        <strong>Close</strong> &mdash; The stream ends naturally after{" "}
        <code>search_results</code> (for search) or <code>message_stop</code>{" "}
        (for LLM). Close the connection on your end.
      </li>
    </ol>

    <h3>Search Event Types</h3>
    <table className="dev-api-params">
      <thead>
        <tr>
          <th>Event</th>
          <th>Data Format</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>search_update</code>
          </td>
          <td>
            <code>
              &#123;"progress": 45, "total": 100, "info": "Searching
              targets..."&#125;
            </code>
          </td>
          <td>
            Progress update during search execution. <code>progress</code> is
            0&ndash;100.
          </td>
        </tr>
        <tr>
          <td>
            <code>search_results</code>
          </td>
          <td>
            <code>
              &#123;"results": &#123;...&#125;, "totals": &#123;...&#125;,
              "page_token": "..."&#125;
            </code>
          </td>
          <td>
            Final search results. Same structure as the synchronous search
            response.
          </td>
        </tr>
        <tr>
          <td>
            <code>search_error</code>
          </td>
          <td>
            <code>&#123;"error": "Error message"&#125;</code>
          </td>
          <td>Search failed. Close the connection after receiving this.</td>
        </tr>
        <tr>
          <td>
            <code>ping</code>
          </td>
          <td>
            <em>(empty or timestamp)</em>
          </td>
          <td>
            Keep-alive signal sent periodically to prevent connection timeout.
          </td>
        </tr>
      </tbody>
    </table>

    <h3>LLM Streaming Event Types</h3>
    <p>
      When your search includes <code>messages</code> (LLM chat), the stream
      delivers the AI response progressively using these events:
    </p>
    <table className="dev-api-params">
      <thead>
        <tr>
          <th>Event</th>
          <th>Data Format</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>message_start</code>
          </td>
          <td>
            <code>&#123;"role": "assistant"&#125;</code>
          </td>
          <td>New LLM response message begins.</td>
        </tr>
        <tr>
          <td>
            <code>content_block_start</code>
          </td>
          <td>
            <code>&#123;"type": "text"&#125;</code>
          </td>
          <td>
            A content block begins. Type is <code>text</code>,{" "}
            <code>tool_use</code>, or <code>tool_result</code>.
          </td>
        </tr>
        <tr>
          <td>
            <code>content_block_delta</code>
          </td>
          <td>
            <code>&#123;"delta": &#123;"text": "chunk..."&#125;&#125;</code>
          </td>
          <td>
            Incremental text content. Append <code>delta.text</code> to build
            the response.
          </td>
        </tr>
        <tr>
          <td>
            <code>content_block_stop</code>
          </td>
          <td>
            <code>&#123;&#125;</code>
          </td>
          <td>Current content block is complete.</td>
        </tr>
        <tr>
          <td>
            <code>message_delta</code>
          </td>
          <td>
            <code>&#123;"stop_reason": "end_turn"&#125;</code>
          </td>
          <td>
            Message metadata update. Includes stop reason when message ends.
          </td>
        </tr>
        <tr>
          <td>
            <code>message_stop</code>
          </td>
          <td>
            <code>&#123;"metrics": &#123;...&#125;&#125;</code>
          </td>
          <td>
            Message complete. Contains usage metrics. Close the connection.
          </td>
        </tr>
        <tr>
          <td>
            <code>message</code>
          </td>
          <td>
            <code>&#123;"role": "assistant", "content": [...]&#125;</code>
          </td>
          <td>Complete assembled message (sent after all deltas).</td>
        </tr>
        <tr>
          <td>
            <code>interrupt</code>
          </td>
          <td>
            <code>&#123;"reason": "..."&#125;</code>
          </td>
          <td>Stream was interrupted (e.g., agent canceled).</td>
        </tr>
        <tr>
          <td>
            <code>metadata</code>
          </td>
          <td>
            <code>&#123;"usage": &#123;...&#125;, "latency": ...&#125;</code>
          </td>
          <td>Session metadata including token usage and latency.</td>
        </tr>
        <tr>
          <td>
            <code>error</code>
          </td>
          <td>
            <code>&#123;"error": "message"&#125;</code>
          </td>
          <td>Error occurred during LLM processing.</td>
        </tr>
      </tbody>
    </table>

    <h3>Tool Use Events</h3>
    <p>
      When the LLM decides to call a tool (search, resolve, members), you will
      see this event sequence:
    </p>
    <pre className="dev-code-pre">
      <code>{`content_block_start  {"type": "tool_use", "tool_use": {"id": "tu_123", "name": "search"}}
content_block_delta  {"delta": {"tool_use": {"input": "{\\"ids\\": [\\"compound:...\\"]}" }}}
content_block_stop   {}
  ... server executes the tool and returns results ...
content_block_start  {"type": "text"}
content_block_delta  {"delta": {"text": "Based on the search results, ..."}}
...`}</code>
    </pre>

    <h3>Full Streaming Client Example</h3>
    <CodeBlock
      title="Complete SSE client with reconnection"
      examples={{
        javascript: `class PlexStreamClient {
  constructor(baseUrl, token) {
    this.baseUrl = baseUrl;
    this.token = token;
  }

  async search(params, onEvent) {
    // 1. Start streaming search (stream=true as query param)
    const resp = await fetch(\`\${this.baseUrl}/api/search?stream=true\`, {
      method: "POST",
      headers: {
        "x-api-key": this.token,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(params)
    });
    const { stream_id } = await resp.json();

    // 2. Connect to SSE (token as query param for EventSource)
    return new Promise((resolve, reject) => {
      const es = new EventSource(
        \`\${this.baseUrl}/api/stream/\${stream_id}?token=\${this.token}\`
      );
      let lastActivity = Date.now();

      const timeoutCheck = setInterval(() => {
        if (Date.now() - lastActivity > 300000) {
          es.close();
          clearInterval(timeoutCheck);
          reject(new Error("Stream timeout: no data received for 5 minutes"));
        }
      }, 30000);

      const handleEvent = (type) => (e) => {
        lastActivity = Date.now();
        const data = e.data ? JSON.parse(e.data) : {};
        onEvent?.(type, data);

        if (type === "search_results" || type === "message_stop") {
          es.close();
          clearInterval(timeoutCheck);
          resolve(data);
        }
        if (type === "search_error" || type === "error") {
          es.close();
          clearInterval(timeoutCheck);
          reject(new Error(data.error || "Stream error"));
        }
      };

      ["search_update", "search_results", "search_error",
       "message_start", "content_block_start", "content_block_delta",
       "content_block_stop", "message_delta", "message_stop",
       "message", "metadata", "interrupt", "error", "ping"
      ].forEach(type => es.addEventListener(type, handleEvent(type)));

      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          clearInterval(timeoutCheck);
          reject(new Error("SSE connection closed unexpectedly"));
        }
      };
    });
  }
}

// Usage
const client = new PlexStreamClient("https://plexsearch.com", token);
const results = await client.search(
  { ids: ["unichem:161671"] },
  (type, data) => console.log(\`[\${type}]\`, data)
);`,
        python: `import json
import requests
from sseclient import SSEClient  # use the bundled sseclient.py

API_URL = "https://plexsearch.com/api"
HEADERS = {"x-api-key": token, "content-type": "application/json"}

# 1. Start streaming search (stream=true as query param)
resp = requests.post(f"{API_URL}/search", params={"stream": True},
                     json={"ids": ["unichem:161671"]}, headers=HEADERS)
stream_id = resp.json()["stream_id"]
stream_url = f"{API_URL}/stream/{stream_id}"

# 2. Connect and iterate events — SSEClient handles reconnection automatically
sse_resp = requests.get(stream_url,
                        headers={**HEADERS, "Accept": "text/event-stream"},
                        stream=True)
for event in SSEClient(sse_resp, events=["*"], reconnect_url=stream_url).events():
    data = json.loads(event.data) if event.data else {}
    print(f"[{event.event}]", data)

    if event.event == "search_results":
        results = data
        break
    elif event.event in ("search_error", "error"):
        raise Exception(data.get("error", "Stream error"))`,
        curl: `# 1. Start streaming search
STREAM_ID=$(curl -s "/api/search?stream=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq -r '.stream_id')

echo "Stream ID: $STREAM_ID"

# 2. Connect to SSE stream
curl -N "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Accept: text/event-stream"

# Each SSE event appears as:
# event: search_update
# data: {"progress": 45, "total": 100, "info": "Searching..."}
#
# event: search_results
# data: {"results": {...}, "totals": {...}}`,
      }}
    />

    <h3>Canceling a Stream</h3>
    <p>
      To cancel an in-progress agent or LLM operation, POST to the cancel
      endpoint with the agent ID:
    </p>
    <CodeBlock
      title="Cancel an agent"
      examples={{
        curl: `curl -s -X POST "/api/agent/AGENT_ID/cancel" \\
  -H "x-api-key: $PLEX_TOKEN"`,
        python: `requests.post(
    f"/api/agent/{agent_id}/cancel",
    headers={"x-api-key": token}
)`,
        javascript: `await fetch(
  \`/api/agent/\${agentId}/cancel\`,
  { method: "POST", headers: { "x-api-key": token } }
);`,
      }}
    />
  </section>
);

export default SseStreaming;
